import { env } from "@/lib/env";

/**
 * backend の認証エンドポイントへの URL 組み立て。
 *
 * SPA 側で OIDC を実装しない。backend が OIDC の当事者であり、
 * `/v1.0/login` へフルページ遷移させれば backend が IdP へ飛ばし、
 * `/v1.0/login_return` で cookie を set して戻してくれる。
 *
 * 相対パスを返すので、dev（vite proxy）でも本番（CloudFront 同一オリジン）でも
 * そのまま動く。
 */

export type BuildLoginUrlOptions = {
  /** ログイン後に戻る絶対 URL。backend 側で prefix 検証される点に注意。 */
  readonly returnTo: string;
  readonly authenticationIdentifier: string;
  readonly basePath?: string;
};

export type BuildLogoutUrlOptions = {
  readonly idToken: string | null;
  readonly authenticationIdentifier: string;
  readonly redirectUrl: string;
  readonly basePath?: string;
};

export function buildLoginUrl({
  returnTo,
  authenticationIdentifier,
  basePath = env.apiBasePath,
}: BuildLoginUrlOptions): string {
  const params = new URLSearchParams({
    authentication_identifier: authenticationIdentifier,
    redirect_url: returnTo,
  });

  return `${basePath}/login?${toQueryString(params)}`;
}

export function buildLogoutUrl({
  idToken,
  authenticationIdentifier,
  redirectUrl,
  basePath = env.apiBasePath,
}: BuildLogoutUrlOptions): string {
  // id_token が無ければ backend のログアウトは成立しない（必須パラメータ）。
  // 破棄するものも無いのでログイン画面へ送るだけにする。
  if (idToken === null) return "/login";

  const params = new URLSearchParams({
    id_token: idToken,
    authentication_identifier: authenticationIdentifier,
    redirect_url: redirectUrl,
  });

  return `${basePath}/logout?${toQueryString(params)}`;
}

/**
 * URLSearchParams はスペースを `+` にするが、backend の redirect_url 検証は
 * 生の URL を prefix 比較するため `%20` に揃えておく。
 */
function toQueryString(params: URLSearchParams): string {
  return params.toString().replaceAll("+", "%20");
}
